import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ProgramLogo from "./ProgramLogo";
import { track } from "../lib/analytics";

const CATEGORY_GRADIENTS = {
  "Play-Based Support": "from-sky-100 via-sky-50 to-amber-50",
  "STEM & Engineering": "from-sky-200 via-cyan-100 to-emerald-50",
  "LEGO Engineering": "from-amber-200 via-orange-100 to-yellow-50",
  "Brick-Based Engineering": "from-amber-200 via-orange-100 to-yellow-50",
  "Robotics & Coding": "from-emerald-200 via-emerald-100 to-sky-50",
  "Summer Camps": "from-orange-200 via-amber-100 to-rose-50",
  "Tennis Programs": "from-lime-200 via-emerald-100 to-sky-50",
  "Kids Activities": "from-rose-200 via-pink-100 to-amber-50",
};

function ProgramArt({ program }) {
  if (program.image_url) {
    return (
      <img
        src={program.image_url}
        alt={program.name}
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }
  if (program.accent_color && program.secondary_color) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${program.secondary_color} 0%, #FFFFFF 80%)`,
        }}
      >
        <ProgramLogo program={program} size={88} rounded="rounded-3xl" />
      </div>
    );
  }
  const grad =
    CATEGORY_GRADIENTS[program.category] ||
    "from-sky-100 via-sky-50 to-amber-50";
  return (
    <div
      className={`absolute inset-0 bg-gradient-to-br ${grad} flex items-center justify-center`}
    >
      <span className="font-display text-3xl font-extrabold tracking-tight text-slate-900/30 px-6 text-center">
        {program.name}
      </span>
    </div>
  );
}

export default function ProgramCard({ program }) {
  if (!program) return null;
  const accent = program.accent_color || "#0369A1";
  const secondary = program.secondary_color || "#E0F2FE";
  return (
    <Link
      to={`/programs/${program.slug}`}
      onClick={() => track("view_program_clicked", { program_slug: program.slug })}
      className="group relative flex flex-col rounded-[1.75rem] overflow-hidden bg-white border border-slate-200/70 hover:border-slate-300 hover:shadow-md transition-all"
      data-testid={`program-card-${program.slug}`}
      style={{ "--accent": accent, "--accent-secondary": secondary }}
    >
      {/* Accent strip top */}
      <span
        className="absolute top-0 left-0 right-0 h-1.5"
        style={{ backgroundColor: accent }}
      />
      <div className="relative aspect-[4/3] overflow-hidden">
        <ProgramArt program={program} />
        {program.program_type === "waitlist" && (
          <span className="absolute top-4 left-4 pill bg-white/95 backdrop-blur-sm text-[10px]" style={{ color: accent }}>
            Coming soon
          </span>
        )}
        {program.seasonal && (
          <span
            className="absolute top-4 left-4 pill text-white text-[10px]"
            style={{ backgroundColor: accent }}
          >
            Seasonal
          </span>
        )}
        {program.brand_label && (
          <span
            className="absolute bottom-4 left-4 pill text-[10px] backdrop-blur-sm"
            style={{ backgroundColor: secondary, color: accent }}
          >
            {program.brand_label}
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-start gap-3">
          <ProgramLogo program={program} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className="pill text-[10px]"
                style={{ backgroundColor: secondary, color: accent }}
              >
                {program.category || "Program"}
              </span>
              <span className="pill bg-slate-100 text-slate-600 text-[10px]">
                {program.age_range || program.ages || "All ages"}
              </span>
            </div>
            <h3 className="mt-2 font-display text-xl font-extrabold tracking-tight text-slate-900 leading-snug">
              {program.name}
            </h3>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-600 line-clamp-3 leading-relaxed">
          {program.short_description || program.tagline || program.description}
        </p>
        {program.class_length && (
          <p className="mt-3 text-xs uppercase tracking-wider font-bold text-slate-500">
            {program.class_length}
          </p>
        )}
        <div
          className="mt-5 flex items-center gap-1.5 text-sm font-semibold transition-colors"
          style={{ color: accent }}
        >
          {program.cta_label || "Learn more"}
          <ArrowUpRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

export { CATEGORY_GRADIENTS, ProgramArt };
